import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { axios } from 'services/axios';
import { useQuery } from 'hooks/useCustomQuery';
import FormBase from '@/components/forms/new/FormBase';
import CustomAttributeFields from './formFields/CustomAttributeFields';
import { Alert, AlertDescription, AlertIcon } from 'components/v4';

// Read the multiChildValue prop (from MultiChild dropdown)
// You can use this value for any logic that needs to react to the dropdown
// Example: log it for now
// (This must come after imports)
// (If you want to use this, place it inside the component, not at the top level)

const DEFAULT_DATA = {
    attribute1: '',
    attribute2: '',
    attribute3: '',
};

const FormDraftSync = ({ values, onValuesChange }) => {
    useEffect(() => {
        onValuesChange(values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);
    return null;
};

/*
 * Custom Fields Form for number blocks edit tab
 */
const CustomFieldsForm = ({
    child,
    onSuccess,
    onBack,
    formDraft = {},
    setFormDraft = () => {},
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const { data: backendData = DEFAULT_DATA, isLoading: loading } = useQuery({
        queryKey: ['numberBlockCustom', child?.id],
        queryFn: async () => {
            const res = await axios.get(`/numberblock/${child.id}`);
            return { ...DEFAULT_DATA, ...res.data };
        },
        enabled: Boolean(child?.id),
    });

    // Reset formDraft when the selected record changes
    useEffect(() => {
        if (child?.id) setFormDraft({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [child?.id]);

    // Always use the latest draft for initialValues unless id changes
    const mergedInitialValues = { ...backendData, ...formDraft };

    // Submit handler
    const handleSubmit = (values) => {
        setIsLoading(true);
        axios
            .put('/numberblock/', { ...values, id: child?.id })
            .then(() => {
                setIsLoading(false);
                setFormDraft({});
                if (onSuccess) onSuccess();
            })
            .catch((err) => {
                setError(err);
                setIsLoading(false);
            });
    };

    // Track form changes
    const handleValuesChange = (values) => {
        setFormDraft(values);
    };

    // Validation function
    const validate = (values) => {
        const errors = {};
        if (values.attribute1 && values.attribute1.length > 100) {
            errors.attribute1 = 'Maximum field length is 100 characters.';
        }
        if (values.attribute2 && values.attribute2.length > 100) {
            errors.attribute2 = 'Maximum field length is 100 characters.';
        }
        if (values.attribute3 && values.attribute3.length > 100) {
            errors.attribute3 = 'Maximum field length is 100 characters.';
        }
        return errors;
    };

    if (loading) return null;

    return (
        <>
            <FormBase
                key="number-block-custom-form"
                initialValues={mergedInitialValues}
                finalFormSubmit={handleSubmit}
                enableReinitialize={true}
                isSubmitDisabled={isLoading}
                onBack={onBack}
                customClick={true}
                validate={validate}>
                {(formikProps) => {
                    const { values, setFieldValue, errors, touched } =
                        formikProps;

                    return (
                        <div>
                            <FormDraftSync
                                values={values}
                                onValuesChange={handleValuesChange}
                            />
                            <CustomAttributeFields
                                darkMode={darkMode}
                                values={values}
                                setFieldValue={setFieldValue}
                                errors={errors}
                                touched={touched}
                            />
                            {error && (
                                <Alert
                                    status="error"
                                    sx={{ marginTop: '1rem' }}>
                                    <AlertIcon />
                                    <AlertDescription>
                                        {error?.response?.data?.message ||
                                            'An error occurred. Please try again later.'}
                                    </AlertDescription>
                                </Alert>
                            )}
                        </div>
                    );
                }}
            </FormBase>
        </>
    );
};

export default CustomFieldsForm;
